import React, { useState, useEffect } from 'react';
import { Button, Col, Form as FormComponent, Row } from 'reactstrap';
import { Field, Form } from 'react-final-form';
import Select from 'react-select';
import { getPractitioners } from '../../../apis/practitioner';
import { useOrganization } from '../../../contexts/organization';
import {
  postPractitionerRole,
  getPractitionerRolesByPractitionerId,
  putPractitionerRole,
  ResponsePayload,
} from '../../../apis/practitionerRole';
import { getDisplayName } from '../../../selectors/practitioner';
import { getReferenceId } from '../../../modules/unsw-fhir/Reference';
import {
  createPractitionerRole,
  IPractitionerRole,
} from '../../../modules/unsw-fhir/PractitionerRole';
import produce from 'immer';

const findPractitionerRoleByOrganizationId = (
  practitionerRole: IPractitionerRole[],
  organizationId: string,
) => {
  return practitionerRole.find(
    pr => getReferenceId(pr.organization) === organizationId,
  );
};

const RenderSelect = ({
  name,
  placeholder,
  disabled,
  options,
  loading,
  searchable,
}: {
  name: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  options?: { value: string; label: string }[];
}) => (
  <Field<{ value: string; label: string }>
    name={name}
    disabled={disabled}
    placeholder={placeholder}>
    {({ input, meta, ...rest }) => (
      <div>
        <Select
          {...input}
          {...rest}
          type="text"
          invalid={!!meta.error}
          options={options}
          isClearable
          isSearchable={searchable}
          isDisabled={disabled}
          isLoading={loading}
        />
        {
          <span
            className={
              meta.error ? 'text-danger visible' : 'text-danger invisible'
            }>
            {meta.error ? meta.error : 'All good'}
          </span>
        }
      </div>
    )}
  </Field>
);

const AddPractitionerButton = (props: {
  className?: string;
  loading?: boolean;
  onClick?: () => void;
}) => {
  return (
    <Button
      color="success"
      type="submit"
      onClick={props.onClick}
      className={`${props.className ? props.className : ''} text-center`}
      disabled={props.loading}>
      Add
    </Button>
  );
};

const AddPractitionerSelect = ({
  onPractitionerRolesUpdate,
  practitionerRoles,
}: {
  practitionerRoles: IPractitionerRole[];
  onPractitionerRolesUpdate: (payload: ResponsePayload) => void;
}) => {
  const [practitioners, setPractitioners] = useState<
    { value: string; label: string }[]
  >([]);
  const { organization } = useOrganization();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const organizationId = organization && organization.id;
        if (!organizationId) return;
        const { data: practitioners } = await getPractitioners();
        const filtered = practitioners.filter(
          practitioner =>
            !practitionerRoles.some(
              pr =>
                getReferenceId(pr.practitioner) === practitioner.id &&
                pr.active,
            ),
        );

        setPractitioners(
          filtered.map(p => ({ value: p.id || '', label: getDisplayName(p) })),
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [organization, practitionerRoles]);

  return (
    <Form<{
      practitioner: { value: string; label: string };
      role: { value: string; label: string };
    }>
      onSubmit={async values => {
        const practitionerId = values.practitioner.value;
        const { value: code, label: display } = values.role;
        const organizationId = organization && organization.id;
        if (!organizationId) return 'Organization Id undefined';

        const { data } = await getPractitionerRolesByPractitionerId(
          practitionerId,
        );

        const oldRole = findPractitionerRoleByOrganizationId(
          data,
          organizationId,
        );

        const newRole = createPractitionerRole(
          practitionerId,
          organizationId,
          {
            code,
            display,
          },
          oldRole && oldRole.id,
        );

        const response = newRole.id
          ? await putPractitionerRole(newRole.id, newRole)
          : await postPractitionerRole(newRole);
        if (!response) return 'failed to create Role';
        onPractitionerRolesUpdate({
          data: produce(practitionerRoles, draft => {
            const index = draft.findIndex(pr => pr.id === response.id);

            if (index < 0) draft.push(response);
            else draft[index] = response;
          }),
        });
      }}
      validate={values => {
        return {
          practitioner: values.practitioner
            ? undefined
            : 'Missing Practitioner',
          role: values.role ? undefined : 'Missing role',
        };
      }}>
      {({ pristine, submitting, handleSubmit, form, invalid }) => (
        <FormComponent
          onSubmit={async event => {
            const error = await handleSubmit(event);
            if (error) {
              return error;
            } else form.reset();
          }}>
          <Row noGutters>
            <Col xs={5}>
              <RenderSelect
                placeholder="Select a practitioner to Add"
                name="practitioner"
                searchable
                loading={loading || submitting}
                disabled={loading || submitting}
                options={practitioners}
              />
            </Col>
            <Col xs={5}>
              <RenderSelect
                placeholder="Select a Role"
                name="role"
                searchable
                loading={loading || submitting}
                disabled={loading || submitting}
                options={[
                  { value: 'nurse', label: 'Nurse' },
                  { value: 'researcher', label: 'Researcher' },
                ]}
              />
            </Col>
            <Col>
              <AddPractitionerButton
                loading={loading || submitting || pristine || invalid}
              />
            </Col>
          </Row>
        </FormComponent>
      )}
    </Form>
  );
};

export default AddPractitionerSelect;
